import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Layers, Save, RefreshCw, ChevronDown, ChevronRight, Plus, Minus } from "lucide-react";

const MOTIVOS = [
  { value: "merma", label: "Merma (caducidad/rotura)" },
  { value: "ajuste", label: "Ajuste de inventario físico" },
  { value: "robo", label: "Robo / extravío" },
  { value: "devolucion_cliente", label: "Devolución cliente" },
  { value: "otro", label: "Otro" },
];

function diasUntil(fecha) {
  if (!fecha) return null;
  const ms = new Date(fecha) - new Date(new Date().toISOString().slice(0, 10));
  return Math.floor(ms / 86400000);
}

function estadoFEFO(fecha, disponible) {
  if (disponible <= 0) return { cls: "border-[#d1dbd6] text-[#5c7368] bg-[#f7f9f8]", txt: "Agotado" };
  const d = diasUntil(fecha);
  if (d == null) return { cls: "border-[#d1dbd6] text-[#5c7368]", txt: "—" };
  if (d < 0) return { cls: "fefo-vencido border", txt: `Vencido (${Math.abs(d)}d)` };
  if (d <= 30) return { cls: "fefo-critico border", txt: `${d}d` };
  if (d <= 180) return { cls: "border-[#f5c477] bg-[#fef7ec] text-[#8a5500]", txt: `${d}d` };
  return { cls: "fefo-safe border", txt: `${d}d` };
}

export default function LotesProductoDialog({ open, onOpenChange, productoId, productoNombre }) {
  const [lotes, setLotes] = useState([]);
  const [provs, setProvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // {loteId: bool}
  const [editForm, setEditForm] = useState({}); // {loteId: {...campos editados}}
  const [savingId, setSavingId] = useState(null);
  const [ajusteOpen, setAjusteOpen] = useState(null); // lote object
  const [ajuste, setAjuste] = useState({ delta: 0, motivo: "merma", observaciones: "" });

  const load = async () => {
    if (!productoId) return;
    setLoading(true);
    try {
      const [{ data: lts }, { data: pv }] = await Promise.all([
        api.get(`/masters/lotes?producto_id=${productoId}`),
        api.get("/masters/proveedores"),
      ]);
      setLotes(lts);
      setProvs(pv);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, productoId]);

  const totalDisp = useMemo(() => lotes.reduce((s, l) => s + (+l.cantidad_disponible || 0), 0), [lotes]);

  const startEdit = (l) => {
    setEditForm(prev => ({
      ...prev,
      [l.id]: {
        numero_lote: l.numero_lote || "",
        fecha_elaboracion: l.fecha_elaboracion || "",
        fecha_vencimiento: l.fecha_vencimiento || "",
        ubicacion: l.ubicacion || "",
        proveedor_id: l.proveedor_id || "",
        registro_sanitario: "",
        observaciones: l.observaciones || "",
      },
    }));
    setExpanded(e => ({ ...e, [l.id]: true }));
  };

  const guardarEdit = async (l) => {
    const data = editForm[l.id];
    if (!data) return;
    try {
      setSavingId(l.id);
      const payload = { ...data };
      if (!payload.registro_sanitario) delete payload.registro_sanitario;
      if (!payload.proveedor_id) payload.proveedor_id = null;
      await api.patch(`/masters/lotes/${l.id}/info`, payload);
      toast.success(`Lote ${data.numero_lote || l.numero_lote} actualizado`);
      setEditForm(prev => { const copy = { ...prev }; delete copy[l.id]; return copy; });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const ejecutarAjuste = async () => {
    if (!ajusteOpen) return;
    const delta = parseInt(ajuste.delta);
    if (!delta || isNaN(delta)) return toast.error("Indique una cantidad distinta de 0");
    try {
      setSavingId(ajusteOpen.id);
      const { data } = await api.post(`/masters/lotes/${ajusteOpen.id}/ajuste`, {
        delta,
        motivo: ajuste.motivo,
        observaciones: ajuste.observaciones || null,
      });
      toast.success(`Stock actualizado: ${data.cantidad_antes} → ${data.cantidad_despues}`);
      setAjusteOpen(null);
      setAjuste({ delta: 0, motivo: "merma", observaciones: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl" data-testid="lotes-producto-dialog">
          <DialogHeader>
            <DialogTitle>
              <Layers size={18} className="inline mr-2 -mt-1" />
              Lotes de {productoNombre}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="rounded-md border border-[#d1dbd6] bg-[#f7f9f8] p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Lotes registrados</div>
              <div className="text-2xl font-bold tabular-nums">{lotes.length}</div>
            </div>
            <div className="rounded-md border border-[#b2d0c2] bg-[#e9f2ee] p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Stock total</div>
              <div className="text-2xl font-bold tabular-nums text-[#205c45]">{totalDisp}</div>
            </div>
            <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex items-end justify-end">
              <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="reload-lotes">
                <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Recargar
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-center text-[#5c7368]">Cargando lotes…</div>
          ) : lotes.length === 0 ? (
            <div className="py-10 text-center text-[#8ba397]">Este producto aún no tiene lotes en bodega.</div>
          ) : (
            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
              {lotes.map(l => {
                const est = estadoFEFO(l.fecha_vencimiento, l.cantidad_disponible);
                const isEditing = !!editForm[l.id];
                const isExpanded = expanded[l.id];
                const data = editForm[l.id] || {};
                return (
                  <div key={l.id} className="rounded-lg border border-[#d1dbd6] bg-white" data-testid={`lote-card-${l.numero_lote}`}>
                    {/* Cabecera */}
                    <button
                      type="button"
                      onClick={() => setExpanded(e => ({ ...e, [l.id]: !e[l.id] }))}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#f7f9f8]"
                    >
                      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <div className="flex-1 min-w-0 grid grid-cols-12 gap-2 items-center text-sm">
                        <div className="col-span-3 font-mono font-semibold truncate">{l.numero_lote}</div>
                        <div className="col-span-3 text-[#5c7368] text-xs">Vence: <span className="text-[#15241d] font-medium">{l.fecha_vencimiento || "—"}</span></div>
                        <div className="col-span-2 text-right">
                          <span className="text-[10px] text-[#5c7368]">Disp.</span>{" "}
                          <span className="font-bold tabular-nums text-base">{l.cantidad_disponible}</span>
                        </div>
                        <div className="col-span-2"><Badge className={`${est.cls} capitalize`}>{est.txt}</Badge></div>
                        <div className="col-span-2 text-[10px] text-[#5c7368] truncate text-right">{l.ubicacion || "—"}</div>
                      </div>
                    </button>

                    {/* Cuerpo expandido */}
                    {isExpanded && (
                      <div className="border-t border-[#d1dbd6] p-3 space-y-3 bg-[#fbfcfb]">
                        {!isEditing ? (
                          <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                              <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Elaboración</div><div>{l.fecha_elaboracion || "—"}</div></div>
                              <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Costo unitario</div><div className="tabular-nums">${(+l.costo_unitario || 0).toFixed(4)}</div></div>
                              <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Cant. inicial</div><div className="tabular-nums">{l.cantidad_inicial}</div></div>
                              <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Bodega</div><div>{l.bodega_id?.slice(0, 8) || "—"}</div></div>
                              {l.observaciones && <div className="col-span-full"><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Observaciones</div><div>{l.observaciones}</div></div>}
                            </div>
                            <div className="flex gap-2 pt-2 border-t border-[#d1dbd6]">
                              <Button size="sm" variant="outline" onClick={() => startEdit(l)} data-testid={`edit-lote-${l.numero_lote}`}>
                                Editar datos
                              </Button>
                              <Button size="sm" variant="outline" className="text-[#b53a25] hover:text-[#b53a25]" onClick={() => { setAjusteOpen(l); setAjuste({ delta: 0, motivo: "merma", observaciones: "" }); }} data-testid={`ajustar-lote-${l.numero_lote}`}>
                                <Minus size={12} className="mr-1" />Ajustar stock
                              </Button>
                            </div>
                          </>
                        ) : (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            <div>
                              <Label className="text-[10px]">Número de lote</Label>
                              <Input className="h-9" value={data.numero_lote} onChange={(e) => setEditForm(p => ({ ...p, [l.id]: { ...p[l.id], numero_lote: e.target.value } }))} />
                            </div>
                            <div>
                              <Label className="text-[10px]">F. elaboración</Label>
                              <Input className="h-9" type="date" value={data.fecha_elaboracion} onChange={(e) => setEditForm(p => ({ ...p, [l.id]: { ...p[l.id], fecha_elaboracion: e.target.value } }))} />
                            </div>
                            <div>
                              <Label className="text-[10px]">F. vencimiento</Label>
                              <Input className="h-9" type="date" value={data.fecha_vencimiento} onChange={(e) => setEditForm(p => ({ ...p, [l.id]: { ...p[l.id], fecha_vencimiento: e.target.value } }))} />
                            </div>
                            <div>
                              <Label className="text-[10px]">Ubicación</Label>
                              <Input className="h-9" value={data.ubicacion} onChange={(e) => setEditForm(p => ({ ...p, [l.id]: { ...p[l.id], ubicacion: e.target.value } }))} placeholder="P5-A3" />
                            </div>
                            <div>
                              <Label className="text-[10px]">Proveedor</Label>
                              <Select value={data.proveedor_id || ""} onValueChange={(v) => setEditForm(p => ({ ...p, [l.id]: { ...p[l.id], proveedor_id: v } }))}>
                                <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                  {provs.map(pr => <SelectItem key={pr.id} value={pr.id}>{pr.razon_social || pr.nombre}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[10px]">Reg. sanitario (actualiza producto)</Label>
                              <Input className="h-9" value={data.registro_sanitario} onChange={(e) => setEditForm(p => ({ ...p, [l.id]: { ...p[l.id], registro_sanitario: e.target.value } }))} placeholder="Solo si cambia" />
                            </div>
                            <div className="col-span-full">
                              <Label className="text-[10px]">Observaciones</Label>
                              <Input className="h-9" value={data.observaciones} onChange={(e) => setEditForm(p => ({ ...p, [l.id]: { ...p[l.id], observaciones: e.target.value } }))} />
                            </div>
                            <div className="col-span-full flex gap-2 pt-1">
                              <Button size="sm" variant="outline" onClick={() => setEditForm(prev => { const c = { ...prev }; delete c[l.id]; return c; })} disabled={savingId === l.id}>
                                Cancelar
                              </Button>
                              <Button size="sm" onClick={() => guardarEdit(l)} disabled={savingId === l.id} className="bg-[#205c45]" data-testid={`save-lote-${l.numero_lote}`}>
                                <Save size={12} className="mr-1" />
                                {savingId === l.id ? "Guardando…" : "Guardar"}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de ajuste de stock */}
      <Dialog open={!!ajusteOpen} onOpenChange={(o) => !o && setAjusteOpen(null)}>
        <DialogContent className="max-w-md" data-testid="ajuste-lote-dialog">
          <DialogHeader>
            <DialogTitle>Ajustar stock · Lote {ajusteOpen?.numero_lote}</DialogTitle>
          </DialogHeader>
          {ajusteOpen && (
            <div className="space-y-3">
              <div className="rounded-md border border-[#d1dbd6] bg-[#f7f9f8] p-3 text-sm">
                <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Disponible actual</div>
                <div className="text-2xl font-bold tabular-nums">{ajusteOpen.cantidad_disponible}</div>
              </div>
              <div>
                <Label>Delta (negativo = baja, positivo = alta)</Label>
                <Input
                  type="number"
                  value={ajuste.delta}
                  onChange={(e) => setAjuste({ ...ajuste, delta: e.target.value })}
                  placeholder="-5  (para restar 5)"
                  className="text-lg h-11 tabular-nums"
                  data-testid="ajuste-delta-input"
                />
                <p className="text-[11px] text-[#5c7368] mt-1">
                  Nuevo stock: <strong>{(parseInt(ajusteOpen.cantidad_disponible) || 0) + (parseInt(ajuste.delta) || 0)}</strong>
                </p>
              </div>
              <div>
                <Label>Motivo</Label>
                <Select value={ajuste.motivo} onValueChange={(v) => setAjuste({ ...ajuste, motivo: v })}>
                  <SelectTrigger data-testid="ajuste-motivo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{MOTIVOS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Observaciones</Label>
                <Input value={ajuste.observaciones} onChange={(e) => setAjuste({ ...ajuste, observaciones: e.target.value })} placeholder="Detalle del ajuste" />
              </div>
              <Badge variant="outline" className="border-[#f5c477] text-[#8a5500] bg-[#fef7ec]">
                Quedará registrado en auditoría y genera movimiento de inventario.
              </Badge>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAjusteOpen(null)} disabled={savingId}>Cancelar</Button>
            <Button onClick={ejecutarAjuste} disabled={savingId} className="bg-[#205c45]" data-testid="confirm-ajuste-btn">
              {parseInt(ajuste.delta) > 0 ? <Plus size={12} className="mr-1" /> : <Minus size={12} className="mr-1" />}
              Aplicar ajuste
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
